import { useState } from 'react';
import { Badge, Box, Button, HStack, IconButton, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { LucidePencil, Plus, Trash2 } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { SingleField } from '@duke-hq/ui/components/SingleField/SingleField';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { trpcAdmin } from '../../../lib/trpcAdmin';
import { SectionCard } from '../components';
import { useCardEdit } from '../useCardEdit';
import { ContactEditForm, type ContactPayload } from './ContactEditForm';
import { testIds } from '../../testIds';
import type { EmployerContact, EmployerDetail } from '../types';

function ContactBlock({
  contact,
  canRemove,
  onEdit,
  onRemove,
}: {
  contact: EmployerContact;
  canRemove: boolean;
  onEdit: () => void;
  onRemove: () => void;
}): React.ReactElement {
  return (
    <Box position="relative" bg="bg.subtle" borderRadius="lg" p={4}>
      <HStack position="absolute" top={4} right={4} gap={2}>
        {contact.isPrimary ? (
          <Badge colorScheme="purple" variant="subtle">
            <Trans>Primary</Trans>
          </Badge>
        ) : null}
        <IconButton
          aria-label={t`Edit contact`}
          icon={<LucidePencil size={iconSize.sm} />}
          size="sm"
          variant="ghost"
          onClick={onEdit}
          data-testid={testIds.editContactButton}
        />
        <IconButton
          aria-label={
            canRemove ? t`Remove contact` : t`Reassign the primary contact before removing this one`
          }
          icon={<Trash2 size={iconSize.sm} />}
          size="sm"
          variant="ghost"
          isDisabled={!canRemove}
          onClick={onRemove}
          data-testid={testIds.removeContactButton}
        />
      </HStack>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <SingleField name={<Trans>First Name</Trans>} value={contact.firstName} />
        <SingleField name={<Trans>Last name</Trans>} value={contact.lastName} />
        <SingleField name={<Trans>Email address</Trans>} value={contact.email} copyToClipboard />
        <SingleField name={<Trans>Phone number</Trans>} value={contact.phone} />
      </SimpleGrid>
    </Box>
  );
}

export function ContactsCard({ employer }: { employer: EmployerDetail }): React.ReactElement {
  const addModal = useCardEdit(undefined, t`Contact added`);
  const editModal = useCardEdit(undefined, t`Contact updated`);
  const deleteModal = useCardEdit(undefined, t`Contact removed`, t`Couldn't remove contact`);
  const [activeContact, setActiveContact] = useState<EmployerContact | null>(null);
  const [contactToRemove, setContactToRemove] = useState<EmployerContact | null>(null);

  const create = trpcAdmin.workPlacements.employers.createEmployerContact.useMutation({
    onSuccess: addModal.onSuccess,
    onError: addModal.onError,
  });
  const update = trpcAdmin.workPlacements.employers.updateEmployerContact.useMutation({
    onSuccess: () => {
      editModal.onSuccess();
      setActiveContact(null);
    },
    onError: editModal.onError,
  });
  const remove = trpcAdmin.workPlacements.employers.deleteEmployerContact.useMutation({
    onSuccess: () => {
      deleteModal.onSuccess();
      setContactToRemove(null);
    },
    onError: deleteModal.onError,
  });

  const handleEdit = (contact: EmployerContact) => {
    setActiveContact(contact);
    editModal.onOpen();
  };

  const handleEditClose = () => {
    editModal.onClose();
    setActiveContact(null);
  };

  const handleSaveEdit = (payload: ContactPayload) => {
    if (activeContact == null) {
      return;
    }
    update.mutate({ contactId: activeContact.id, ...payload });
  };

  const handleSaveAdd = (payload: ContactPayload) => {
    create.mutate({ employerId: employer.id, ...payload });
  };

  const handleRemoveConfirm = () => {
    if (contactToRemove == null) {
      return;
    }
    remove.mutate({ contactId: contactToRemove.id });
  };

  const contactToRemoveName =
    contactToRemove != null
      ? `${contactToRemove.firstName} ${contactToRemove.lastName}`.trim()
      : '';

  return (
    <SectionCard
      title={<Trans>Contacts</Trans>}
      data-testid={testIds.drawerContactsSection}
      action={
        <Button
          leftIcon={<Plus size={iconSize.sm} />}
          variant="ghost"
          onClick={addModal.onOpen}
          data-testid={testIds.addContactButton}
        >
          <Trans>Add contact</Trans>
        </Button>
      }
    >
      {employer.contacts.length === 0 ? (
        <Text fontSize="sm" color="fg.muted">
          <Trans>No contacts yet</Trans>
        </Text>
      ) : (
        <Stack gap={3}>
          {employer.contacts.map((contact) => (
            <ContactBlock
              key={contact.id}
              contact={contact}
              canRemove={!contact.isPrimary || employer.contacts.length === 1}
              onEdit={() => handleEdit(contact)}
              onRemove={() => setContactToRemove(contact)}
            />
          ))}
        </Stack>
      )}

      <FormModalWrapper
        isOpen={addModal.isOpen}
        onClose={addModal.onClose}
        modalContentHeight="auto"
        modalContent={
          <ContactEditForm
            onSave={handleSaveAdd}
            onCancel={addModal.onClose}
            isSaving={create.isPending}
          />
        }
      />

      <FormModalWrapper
        isOpen={editModal.isOpen}
        onClose={handleEditClose}
        modalContentHeight="auto"
        modalContent={
          <ContactEditForm
            initialContact={activeContact ?? undefined}
            onSave={handleSaveEdit}
            onCancel={handleEditClose}
            isSaving={update.isPending}
          />
        }
      />

      <ConfirmationDialog
        isOpen={contactToRemove != null}
        onClose={() => setContactToRemove(null)}
        title={<Trans>Remove contact?</Trans>}
        body={
          <Trans>
            Remove {contactToRemoveName} from this employer&apos;s contacts? This can&apos;t be
            undone.
          </Trans>
        }
        confirmLabel={<Trans>Remove</Trans>}
        onConfirm={handleRemoveConfirm}
        isLoading={remove.isPending}
        variant="destructive"
      />
    </SectionCard>
  );
}
